import { differenceInCalendarDays, differenceInMinutes, format, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';

export function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function endOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/** Inclusive calendar days between start-of-day(s) and start-of-day(e). */
export function formatAllDayDurationLabel(start: Date, end: Date): string {
  const s0 = startOfDay(start).getTime();
  const e0 = startOfDay(end).getTime();
  const days = differenceInCalendarDays(new Date(e0), new Date(s0)) + 1;
  if (days <= 1) return 'All day';
  return `${days} days`;
}

/** Human-readable duration for backend `trajanje` (computed, not edited). */
export function formatDurationLabel(start: Date, end: Date): string {
  let mins = differenceInMinutes(end, start);
  if (Number.isNaN(mins) || mins < 0) mins = 0;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h} hr`;
  return `${h} hr ${m} min`;
}

export function mergeDatePart(base: Date, fromPicker: Date | null): Date {
  if (!fromPicker) return new Date(base);
  const x = new Date(base);
  x.setFullYear(fromPicker.getFullYear(), fromPicker.getMonth(), fromPicker.getDate());
  return x;
}

export function mergeTimePart(base: Date, fromPicker: Date | null): Date {
  if (!fromPicker) return new Date(base);
  const x = new Date(base);
  x.setHours(fromPicker.getHours(), fromPicker.getMinutes(), 0, 0);
  return x;
}

export function parseEventStartEnd(event: {
  trajanje_start?: string;
  trajanje_end?: string;
}): { start: Date; end: Date } {
  const now = new Date();
  let start = now;
  if (event.trajanje_start) {
    const d = parseISO(event.trajanje_start);
    if (!Number.isNaN(d.getTime())) start = d;
  }
  let end = new Date(start.getTime() + 60 * 60 * 1000);
  if (event.trajanje_end) {
    const d = parseISO(event.trajanje_end);
    if (!Number.isNaN(d.getTime())) end = d;
  }
  if (end.getTime() <= start.getTime()) {
    end = new Date(start.getTime() + 60 * 60 * 1000);
  }
  return { start, end };
}

/** Display date for readers, e.g. "Apr 11, 2026". */
export function formatEventDatumDisplay(d: Date): string {
  return format(d, 'MMM d, yyyy', { locale: enUS });
}

export function buildEventDatetimeFields(
  notebookId: string,
  start: Date,
  end: Date,
  prevDedup: string,
  options?: { allDay?: boolean }
): {
  datum: string;
  ura: string;
  trajanje: string;
  datum_parsed: string;
  trajanje_start: string;
  trajanje_end: string;
  dedup_key: string;
} {
  const allDay = Boolean(options?.allDay);
  let s = new Date(start);
  let e = new Date(end);
  if (allDay) {
    s = startOfDay(s);
    e = endOfDay(e);
    if (e.getTime() < s.getTime()) {
      e = endOfDay(s);
    }
    const trajanje = formatAllDayDurationLabel(s, e);
    return {
      datum: formatEventDatumDisplay(s),
      ura: '',
      trajanje,
      datum_parsed: format(s, 'yyyy-MM-dd'),
      trajanje_start: s.toISOString(),
      trajanje_end: e.toISOString(),
      dedup_key: prevDedup?.trim() || `draft-${notebookId}-${format(s, 'yyyyMMdd')}`,
    };
  }
  const trajanje = formatDurationLabel(s, e);
  return {
    datum: formatEventDatumDisplay(s),
    ura: format(s, 'HH:mm'),
    trajanje,
    datum_parsed: format(s, 'yyyy-MM-dd'),
    trajanje_start: s.toISOString(),
    trajanje_end: e.toISOString(),
    dedup_key: prevDedup?.trim() || `draft-${notebookId}-${format(s, 'yyyyMMdd-HHmm')}`,
  };
}
